import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { ChevronLeft } from 'lucide-react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { MobileListViewHeader } from 'components/MobileListViewHeader'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import { MobileInfiniteScrollView } from 'components/MobileInfiniteScrollView'
import { MobileNoteCard } from 'components/Notes/MobileNoteCard'
import { MobileColors } from 'styles/mobileTokens'
import { FbLink } from 'components/FbUI/FbLink'
import apiService from 'services/api'
import { INote } from 'models/contact_companies'
import { PaginatedResponse } from 'models/paginated-response'
import { BackButton } from 'components/Mobile/Mobile.styles'
import { formatInteger } from 'utils/formatting'
import { Tabs } from 'components/Tabs'

const NOTES_TABLE_KEY = 'mobile-notes-table'
const IDENTIFIER = 'MOBILE_NOTES_TABLE' as const

const sortingOptions = [
  {
    label: <SortingOptionLabel label="Last Updated" type="number" desc />,
    value: '-modified',
  },
  {
    label: <SortingOptionLabel label="Last Updated" type="number" />,
    value: 'modified',
  },
  {
    label: <SortingOptionLabel label="Date Created" type="number" desc />,
    value: '-created',
  },
  {
    label: <SortingOptionLabel label="Date Created" type="number" />,
    value: 'created',
  },
  {
    label: <SortingOptionLabel label="Author" />,
    value: 'user',
  },
  {
    label: <SortingOptionLabel label="Author" desc />,
    value: '-user',
  },
]

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const HeaderSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #191d23;
`

const CompanyLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`

const ListArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

export function MobileNotesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const api = apiService()

  // Query params
  const chainIdParam = searchParams.get('chain_id')
  const contactCompanyIdParam = searchParams.get('contact_company_id')
  const doorProxyIdParam = searchParams.get('door_proxy_id')
  const companyName = searchParams.get('company_name')
  const doorName = searchParams.get('door_name')

  const chainId = chainIdParam ? Number(chainIdParam) : undefined
  const contactCompanyId = contactCompanyIdParam
    ? Number(contactCompanyIdParam)
    : undefined
  const doorProxyId = doorProxyIdParam ? Number(doorProxyIdParam) : undefined

  // Determine if this is a company page (show tabs) or door page (no tabs)
  const isCompanyPage = !doorProxyId && (!!chainId || !!contactCompanyId)

  const [selectedTab, setSelectedTab] = useState<'company' | 'door'>('company')

  // counts stored in state
  const [companyNotesCount, setCompanyNotesCount] = useState(0)
  const [doorNotesCount, setDoorNotesCount] = useState(0)

  const notesFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { sorting } = dataTableSortingStoreRepo.getStore(NOTES_TABLE_KEY)()
  const filtersAsParams = useFilterParams(notesFilterStore)

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({ ...filtersAsParams, ...sortParams }),
    [filtersAsParams, sortParams]
  )

  const noteType = isCompanyPage ? selectedTab : undefined

  const query = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: [
      'notes-infinite',
      chainId,
      contactCompanyId,
      doorProxyId,
      noteType,
      filterAndSortParams,
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const params = {
        ...filterAndSortParams,
        page: pageParam,
        limit: 20,
      }
      const res = await api.getNotes(
        chainId,
        contactCompanyId,
        params,
        undefined,
        doorProxyId,
        noteType
      )

      if (noteType === 'company') {
        setCompanyNotesCount(res.count)
      } else if (noteType === 'door') {
        setDoorNotesCount(res.count)
      }

      return res
    },
    getNextPageParam: (
      lastPage: PaginatedResponse<INote>,
      _allPages,
      lastPageParam
    ) => {
      const totalPages = Math.ceil(lastPage.count / 20)
      return lastPageParam < totalPages ? lastPageParam + 1 : undefined
    },
    enabled: !!(chainId || contactCompanyId || doorProxyId),
    refetchOnWindowFocus: 'always',
    staleTime: 0,
  })

  // preload counts on mount (page 1 only)
  useEffect(() => {
    const params = {
      ...filterAndSortParams,
      page: 1,
      limit: 1,
    }

    if (chainId || contactCompanyId) {
      void api
        .getNotes(
          chainId,
          contactCompanyId,
          params,
          undefined,
          undefined,
          'company'
        )
        .then((res) => setCompanyNotesCount(res.count))
        .catch(() => {})
    }

    void api
      .getNotes(
        chainId,
        contactCompanyId,
        params,
        undefined,
        doorProxyId,
        'door'
      )
      .then((res) => setDoorNotesCount(res.count))
      .catch(() => {})
  }, [chainId, contactCompanyId, doorProxyId])

  const handleBack = () => {
    navigate(-1)
  }

  const handleGoToEdit = (note: INote) => {
    const params = searchParams.toString()
    navigate(`/notes/${note.id}/edit${params ? `?${params}` : ''}`, {
      state: { note },
    })
  }

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft size={20} />
        Back
      </BackButton>

      <HeaderSection>
        <PageTitle>Notes</PageTitle>
        {(companyName || doorName) && (
          <CompanyLinks>
            {companyName && contactCompanyId && (
              <FbLink to={`/company/${contactCompanyId}`}>{companyName}</FbLink>
            )}
            {doorName && doorProxyId && (
              <>
                {companyName && <span>|</span>}
                <FbLink to={`/door/${doorProxyId}`}>{doorName}</FbLink>
              </>
            )}
          </CompanyLinks>
        )}
      </HeaderSection>

      {isCompanyPage && (
        <div className="w-full mt-3 ml-2">
          <Tabs<'company' | 'door'>
            tabs={[
              {
                key: 'company',
                title: `Company Notes (${formatInteger(companyNotesCount)})`,
              },
              {
                key: 'door',
                title: `Door Notes (${formatInteger(doorNotesCount)})`,
              },
            ]}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            className="border-b border-gray-200"
          />
        </div>
      )}

      {!isCompanyPage && !!doorProxyId && (
        <div className="px-4 pt-2 text-sm text-slate-600">
          Door Notes ({formatInteger(doorNotesCount)})
        </div>
      )}

      <MobileListViewHeader
        sortingOptions={sortingOptions}
        filterStoreIdentifier={IDENTIFIER}
        tableKey={NOTES_TABLE_KEY}
        defaultSorting={[{ id: 'modified', desc: true }]}
      />

      <FilterChips
        store={notesFilterStore}
        identifier={IDENTIFIER}
        clearAllButton
        showActive
      />

      <ListArea>
        {query.isLoading && (
          <div className="px-4 py-2 text-sm text-slate-500">Loading notes…</div>
        )}

        <MobileInfiniteScrollView<PaginatedResponse<INote>, INote>
          extractItems={(page) => page.results}
          infiniteQuery={query}
        >
          {(notes) =>
            notes?.map((note: INote) => (
              <MobileNoteCard
                key={note.id}
                note={note}
                onEdit={handleGoToEdit}
              />
            ))
          }
        </MobileInfiniteScrollView>
      </ListArea>
    </PageContainer>
  )
}
